import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getChatSessionsWithLatestMessage,
  markMessagesAsRead,
  type ChatSessionsWithLatestResponse,
} from '@/services/chat';
import { webSocketService } from '@/services/websocket';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { chatEventBridge } from '@/utils/chatEventBridge';

export const useChatSessionsList = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);

  // Query for chat sessions with latest message
  const {
    data: sessionsData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['chat-sessions-with-latest', user?.id],
    queryFn: getChatSessionsWithLatestMessage,
    enabled: !!user?.id,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 30 * 1000, // Refetch every 30 seconds as fallback
  });

  // Real-time event handlers
  const handleSessionsListUpdated = useCallback((data: {
    sessionId: string;
    latestMessage: any;
    unreadCount: number;
    timestamp: Date;
  }) => {
    // Update the sessions list with new message data
    queryClient.setQueryData(['chat-sessions-with-latest', user?.id], (oldData: ChatSessionsWithLatestResponse | undefined) => {
      if (!oldData) return oldData;

      const updatedSessions = oldData.sessions.map(sessionData => {
        if (sessionData.session.id === data.sessionId) {
          return {
            ...sessionData,
            latestMessage: data.latestMessage,
            unreadCount: data.unreadCount,
            lastActivityAt: data.latestMessage.sentAt || sessionData.lastActivityAt
          };
        }
        return sessionData;
      });

      // Sort by last activity (latest first)
      updatedSessions.sort((a, b) =>
        new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime()
      );

      return {
        ...oldData,
        sessions: updatedSessions
      };
    });
  }, [queryClient, user?.id]);

  const handleUnreadCountUpdated = useCallback((data: {
    sessionId: string;
    unreadCount: number;
    timestamp: Date;
  }) => {
    // Update unread count for specific session
    queryClient.setQueryData(['chat-sessions-with-latest', user?.id], (oldData: ChatSessionsWithLatestResponse | undefined) => {
      if (!oldData) return oldData;

      const updatedSessions = oldData.sessions.map(sessionData => {
        if (sessionData.session.id === data.sessionId) {
          return {
            ...sessionData,
            unreadCount: data.unreadCount
          };
        }
        return sessionData;
      });

      return {
        ...oldData,
        sessions: updatedSessions
      };
    });
  }, [queryClient, user?.id]);

  // Handle new messages directly (when ChatSidebar is closed)
  const handleNewMessage = useCallback((payload: { message: any }) => {
    const message = payload.message;
    console.log('New message received in useChatSessionsList:', message);

    // Update the session list with this new message
    queryClient.setQueryData(['chat-sessions-with-latest', user?.id], (oldData: ChatSessionsWithLatestResponse | undefined) => {
      if (!oldData) return oldData;

      const updatedSessions = oldData.sessions.map(sessionData => {
        if (sessionData.session.id === message.sessionId) {
          // Calculate new unread count
          const isFromCurrentUser = message.senderId === user?.id;
          const newUnreadCount = isFromCurrentUser ? sessionData.unreadCount : sessionData.unreadCount + 1;

          return {
            ...sessionData,
            latestMessage: {
              id: message.id,
              message: message.message,
              messageType: message.messageType,
              sentAt: message.sentAt || message.createdAt,
              senderId: message.senderId,
              isRead: false
            },
            unreadCount: newUnreadCount,
            lastActivityAt: message.sentAt || message.createdAt
          };
        }
        return sessionData;
      });

      // Sort by last activity (latest first)
      updatedSessions.sort((a, b) =>
        new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime()
      );

      return {
        ...oldData,
        sessions: updatedSessions
      };
    });
  }, [queryClient, user?.id]);

  // Handle new message notifications
  const handleNewMessageNotification = useCallback((data: {
    sessionId: string;
    message: string;
    senderId: string;
  }) => {
    console.log('New message notification received:', data);
    // Trigger a refetch to get updated data
    refetch();
  }, [refetch]);

  // **BRIDGE: Handle chat messages from notification system**
  const handleBridgedChatMessage = useCallback((data: {
    sessionId: string;
    senderId: string;
    senderName: string;
    message: string;
    messageType: string;
    sentAt: string;
  }) => {
    console.log('🌉 Bridged chat message received:', data);

    // Update the session list with this new message
    queryClient.setQueryData(['chat-sessions-with-latest', user?.id], (oldData: ChatSessionsWithLatestResponse | undefined) => {
      if (!oldData) return oldData;

      let sessionFound = false;
      const updatedSessions = oldData.sessions.map(sessionData => {
        if (sessionData.session.id === data.sessionId) {
          sessionFound = true;
          // Calculate new unread count
          const isFromCurrentUser = data.senderId === user?.id;
          const newUnreadCount = isFromCurrentUser ? sessionData.unreadCount : sessionData.unreadCount + 1;

          return {
            ...sessionData,
            latestMessage: {
              id: `temp-${Date.now()}`, // Temporary ID since we don't have the real message ID
              message: data.message,
              messageType: data.messageType,
              sentAt: data.sentAt,
              senderId: data.senderId,
              isRead: false
            },
            unreadCount: newUnreadCount,
            lastActivityAt: data.sentAt
          };
        }
        return sessionData;
      });

      // If session not found, trigger a refetch to get the new session
      if (!sessionFound) {
        console.log('🔄 Session not found in current list, triggering refetch');
        setTimeout(() => refetch(), 1000);
        return oldData;
      }

      // Sort by last activity (latest first)
      updatedSessions.sort((a, b) =>
        new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime()
      );

      return {
        ...oldData,
        sessions: updatedSessions
      };
    });
  }, [queryClient, user?.id, refetch]);

  // Setup WebSocket event listeners and bridge
  useEffect(() => {
    console.log('🔍 useChatSessionsList: Setting up WebSocket listeners and bridge');
    console.log('🔍 WebSocket connected:', webSocketService.isConnected());

    // **BRIDGE: Always set up notification bridge (this always works)**
    console.log('🌉 Setting up notification bridge listener');
    chatEventBridge.on('chat_message_received', handleBridgedChatMessage);

    // Try to set up direct WebSocket listeners (fallback if bridge doesn't work)
    if (webSocketService.isConnected()) {
      console.log('✅ Setting up direct chat WebSocket listeners');
      setIsConnected(true);

      const handlerId = webSocketService.addChatHandlers({
        onSessionsListUpdated: handleSessionsListUpdated,
        onUnreadCountUpdated: handleUnreadCountUpdated,
        onNewMessage: handleNewMessage,
        onNewMessageNotification: handleNewMessageNotification,
      });

      return () => {
        // Clean up both bridge and direct WebSocket listeners
        chatEventBridge.off('chat_message_received', handleBridgedChatMessage);
        if (handlerId) {
          webSocketService.removeChatHandlers(handlerId);
        }
        setIsConnected(false);
      };
    } else {
      console.log('⚠️ WebSocket not connected, using notification bridge only');
      setIsConnected(false);

      return () => {
        chatEventBridge.off('chat_message_received', handleBridgedChatMessage);
      };
    }
  }, [handleSessionsListUpdated, handleUnreadCountUpdated, handleNewMessage, handleNewMessageNotification, handleBridgedChatMessage]);

  // Mark session messages as read
  const markSessionAsRead = useCallback(async (sessionId: string) => {
    try {
      await markMessagesAsRead(sessionId);

      // Optimistically update the unread count
      queryClient.setQueryData(['chat-sessions-with-latest', user?.id], (oldData: ChatSessionsWithLatestResponse | undefined) => {
        if (!oldData) return oldData;

        const updatedSessions = oldData.sessions.map(sessionData => {
          if (sessionData.session.id === sessionId) {
            return {
              ...sessionData,
              unreadCount: 0
            };
          }
          return sessionData;
        });

        return {
          ...oldData,
          sessions: updatedSessions
        };
      });

      // toast({
      //   title: "Success",
      //   description: "Messages marked as read",
      //   variant: "default",
      // });
    } catch (error) {
      console.error('Error marking messages as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark messages as read",
        variant: "destructive",
      });
    }
  }, [queryClient, user?.id, toast]);

  // Get total unread message count across all sessions
  const totalUnreadCount = sessionsData?.sessions.reduce((total, session) => {
    return total + session.unreadCount;
  }, 0) || 0;

  // Get sessions sorted by last activity
  const sessions = sessionsData?.sessions || [];

  return {
    sessions,
    totalUnreadCount,
    isLoading,
    error,
    isConnected,
    refetch,
    markSessionAsRead,
  };
};